import { Dispatch, ReactNode, SetStateAction, createContext, useCallback, useContext, useEffect, useState } from 'react'
import { FileService, ImageType } from '@api/file-service'
import { ConsentContext } from '@context/ConsentContext'
import { PredictionContext } from '@context/PredictionContext'
import { SurveyContext } from '@context/SurveyContext'
import { renderCanvasToBase64, renderCanvasToBase64UsingBaseImage } from '@helpers/share'
import { APP_SLUG } from '@models/AppType'
import { SurveyStep } from '@models/Survey/SurveyStep'
import dayjs from 'dayjs'

export interface ShareContextProps {
  shareModalOpened: boolean,
  handleOpenShareModal: () => void,
  handleCloseShareModal: () => void,
  url?: string,
  datagotchiImgUrl?: string,
  datagotchiImgFile?: File,
  datagotchiImgId?: string,
  datagotchiImg?: string,
  isLoading: boolean,
  setImageHasBeenSet?: Dispatch<SetStateAction<boolean>>,
}

export const initialShareContext: ShareContextProps = {
  shareModalOpened: false,
  handleOpenShareModal: () => { },
  handleCloseShareModal: () => { },
  datagotchiImg: '',
  isLoading: false,
}
export const ShareContext = createContext<ReturnType<typeof useProvideShare>>(initialShareContext)

export function ProvideShare({ children }: { children: ReactNode }): JSX.Element {
  const currentShare = useProvideShare()
  return <ShareContext.Provider value={currentShare}>{children}</ShareContext.Provider>
}

export const useShare = () => useContext(ShareContext)

const useProvideShare = (): ShareContextProps => {
  const { consentData, isMajor } = useContext(ConsentContext)
  const { step } = useContext(SurveyContext)
  const { results, sandboxedResults } = useContext(PredictionContext)

  const [shareModalOpened, setShareModalOpened] = useState(false)
  const [url, setUrl] = useState<string | undefined>()

  const [datagotchiImgUrl, setDatagotchiImgUrl] = useState<string | undefined>()
  const [datagotchiImgFile, setDatagotchiImgFile] = useState<File | undefined>()
  const [datagotchiImg, setDatagotchiImg] = useState<string | undefined>()
  const [datagotchiImgId, setDatagotchiImgId] = useState<string | undefined>()

  const [isLoading, setIsLoading] = useState(false)

  const [imageHasBeenSet, setImageHasBeenSet] = useState<boolean>(false)

  const canShare =
    (sandboxedResults || results) &&
    (step === SurveyStep.Prediction || step === SurveyStep.Results || step === SurveyStep.Sandbox)

  const handleOpenShareModal = () => {
    setShareModalOpened(true)
  }

  const handleCloseShareModal = () => {
    setShareModalOpened(false)
  }

  const getProfileImageSrc = useCallback(async () => {
    setIsLoading(true)

    const visitorId = consentData?.visitorId

    if (!visitorId) {
      setIsLoading(false)
      return
    }

    let imgId = datagotchiImgId
    if (!imgId) {
      imgId = isMajor ? visitorId : crypto.randomUUID()
      setDatagotchiImgId(imgId)
    }

    let portraitDataUrl: string | undefined

    if (step === SurveyStep.Prediction) {
      if (!imageHasBeenSet) {
        setIsLoading(false)
        return
      }

      const img = document.getElementById('portrait') as HTMLImageElement
      if (!img) {
        setIsLoading(false)
        return
      }

      const base64Data = await renderCanvasToBase64UsingBaseImage(img)

      if (!base64Data) {
        setIsLoading(false)
        return
      }

      portraitDataUrl = base64Data
      try {
        await FileService.uploadBase64Image(imgId, ImageType.FULL_IMAGE, base64Data)
      } catch {
        /* Upload peut être indisponible en local ; on retombe sur portraitDataUrl si le CDN ne sert pas le fichier. */
      }
    } else if (step === SurveyStep.Results || step === SurveyStep.Sandbox) {
      const img = document.getElementById('portrait') as HTMLImageElement | null
      if (img) {
        const base64Data = await renderCanvasToBase64UsingBaseImage(img)
        if (base64Data) {
          portraitDataUrl = base64Data
          try {
            await FileService.uploadBase64Image(imgId, ImageType.FULL_IMAGE, base64Data)
          } catch {
            /* idem */
          }
        }
      }
    }

    const datagotchiImgUrl = `${import.meta.env.VITE_APP_CDN_BASE_URL}/images/full-image/${imgId}.png`

    const response = await fetch(datagotchiImgUrl)

    let blob: Blob
    let base64data: string

    if (response.ok) {
      blob = await response.blob()
      base64data = await new Promise<string>((resolve) => {
        const reader = new FileReader()
        reader.onloadend = () => {
          resolve(reader.result as string)
        }
        reader.readAsDataURL(blob)
      })
    } else if (portraitDataUrl) {
      base64data = portraitDataUrl
      const fromDataUrl = await fetch(portraitDataUrl)
      blob = await fromDataUrl.blob()
    } else {
      setIsLoading(false)
      return
    }

    const file = new File([blob], `datagotchi-${dayjs().format('YYYY-MM-DD')}.png`, {
      type: blob.type || 'image/png',
    })

    setUrl(`https://share.${import.meta.env.VITE_APP_BASE_HOSTNAME}?visitorId=${imgId}&app=${APP_SLUG}`)
    setDatagotchiImgUrl(datagotchiImgUrl)
    setDatagotchiImgFile(file)
    setDatagotchiImg(base64data)
    setIsLoading(false)
  }, [consentData, step, imageHasBeenSet])

  useEffect(() => {
    if (!canShare) {
      return
    }

    void getProfileImageSrc()
  }, [canShare, imageHasBeenSet])

  return {
    shareModalOpened,
    handleOpenShareModal,
    handleCloseShareModal,
    url,
    datagotchiImgUrl,
    datagotchiImgFile,
    datagotchiImg,
    datagotchiImgId,
    isLoading,
    setImageHasBeenSet,
  }
}
